/**
 * EditJobPage - full sync editor.
 *
 * Re-introspects the source so the user can:
 *   • rename the sync,
 *   • change the frequency + active state,
 *   • RE-SELECT which table to sync,
 *   • re-map columns (include / type / time / entity),
 *   • re-scope the backfill (all / most-recent N / date range).
 *
 * Falls back to a lightweight editor for non-tabular sources (REST/Kafka).
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, TextField, Stack, Button, Switch, FormControlLabel,
  CircularProgress, Alert, Divider, ToggleButton, ToggleButtonGroup, MenuItem,
  useTheme,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import { FREQUENCIES, scheduleToFrequency, scopeToBackfill } from './connectorMeta';
import TableCard from './TableCard';
import {
  fetchRequest as editJobLoadRequest,
  clear as clearEditJobLoad,
} from '../../redux/actions/datasources/editJobLoad';
import {
  fetchRequest as updateJobRequest,
  clear as clearUpdateJob,
} from '../../redux/actions/datasources/updateJob';
import { fetchRequest as triggerJobRequest } from '../../redux/actions/datasources/triggerJob';

const EMPTY_SLICE = { response: null, isLoading: false, error: null };

const FREQ_SCHEDULE = {
  realtime: { type: 'interval', interval_seconds: 60, enabled: true },
  '15m':    { type: 'interval', interval_seconds: 900, enabled: true },
  hourly:   { type: 'cron', cron_expression: '0 * * * *', timezone: 'UTC', enabled: true },
  daily:    { type: 'cron', cron_expression: '0 2 * * *', timezone: 'UTC', enabled: true },
  manual:   { type: 'once', enabled: false },
};

// Apply a job's saved field_mappings onto a freshly-introspected table so the
// editor opens reflecting the current config (not just the auto-map).
const mergeMappings = (table, fieldMappings) => {
  if (!table) return table;
  const byField = {};
  (fieldMappings || []).forEach((m) => { byField[m.source_field] = m; });
  const columns = table.columns.map((c) => {
    const m = byField[c.source_field];
    if (!m) return { ...c, included: false };
    return { ...c, included: true, data_type: m.data_type || c.data_type, is_timestamp: !!m.is_timestamp, is_entity: !!m.is_entity };
  });
  const ts = columns.find((c) => c.is_timestamp);
  const ent = columns.find((c) => c.is_entity);
  return { ...table, columns, timestamp_column: ts?.source_field || null, entity_column: ent?.source_field || null };
};

export default function EditJobPage() {
  const { sourceId, jobId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [job, setJob] = useState(null);
  const [tablesList, setTablesList] = useState([]);

  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState('hourly');
  const [active, setActive] = useState(true);
  const [tableName, setTableName] = useState('');
  const [editTable, setEditTable] = useState(null);
  const [scope, setScope] = useState({ mode: 'auto' });

  const loadSlice = useSelector((s) => s.datasource_edit_job_load) || EMPTY_SLICE;
  const updateSlice = useSelector((s) => s.datasource_update_job) || EMPTY_SLICE;

  useEffect(() => {
    // The editJobLoad saga fetches the jobs list and re-introspects the
    // source in parallel (a failed introspection is tolerated).
    dispatch(editJobLoadRequest({ sourceId }));
    return () => {
      dispatch(clearEditJobLoad());
      dispatch(clearUpdateJob());
    };
  }, [sourceId, jobId, dispatch]);

  useEffect(() => {
    const resp = loadSlice.response;
    if (!resp) return;
    const { jobs, inspect } = resp;
    const found = jobs.find((j) => j.job_id === jobId);
    if (!found) { setError('Job not found.'); setLoading(false); return; }
    setJob(found);
    setName(found.name || '');
    setFrequency(scheduleToFrequency(found.schedule));
    setActive(found.status !== 'paused' && found.status !== 'inactive');

    const tbls = inspect?.tables || [];
    setTablesList(tbls);

    const currentTable = found.config?.table;
    if (currentTable && tbls.length) {
      const match = tbls.find((t) => t.table_name === currentTable) || tbls[0];
      setTableName(match.table_name);
      setEditTable(match.table_name === currentTable
        ? mergeMappings(match, found.config?.field_mappings)
        : match);
    }
    setLoading(false);
  }, [loadSlice.response, jobId]);

  useEffect(() => {
    if (loadSlice.error) {
      setError(loadSlice.error);
      setLoading(false);
    }
  }, [loadSlice.error]);

  const onSelectTable = (newName) => {
    setTableName(newName);
    const t = tablesList.find((x) => x.table_name === newName);
    // Re-apply saved mappings only when re-selecting the original table.
    setEditTable(newName === job?.config?.table ? mergeMappings(t, job?.config?.field_mappings) : t);
    setScope({ mode: 'auto' });
  };

  const updateColumn = (idx, patch) => {
    setEditTable((t) => {
      if (!t) return t;
      const columns = t.columns.map((c, i) => {
        if (i !== idx) {
          if (patch.is_timestamp) return { ...c, is_timestamp: false };
          if (patch.is_entity) return { ...c, is_entity: false };
          return c;
        }
        return { ...c, ...patch };
      });
      const ts = columns.find((c) => c.is_timestamp);
      const ent = columns.find((c) => c.is_entity);
      return { ...t, columns, timestamp_column: ts?.source_field || null, entity_column: ent?.source_field || null };
    });
  };

  const save = () => {
    setSaving(true); setError(null);
    const body = {
      name,
      schedule: FREQ_SCHEDULE[frequency] || FREQ_SCHEDULE.hourly,
      status: active ? 'active' : 'paused',
    };

    // Tabular sources: rebuild the full job config from the editor.
    if (editTable) {
      const cols = editTable.columns.filter((c) => c.included || c.is_timestamp || c.is_entity);
      const field_mappings = cols.map((c) => ({
        source_field: c.source_field, target_key: c.target_key, data_type: c.data_type,
        display_name: c.display_name || undefined,
        is_timestamp: c.is_timestamp, is_entity: c.is_entity, is_event: c.is_event || false, parse_options: {},
      }));
      const tsCol = editTable.columns.find((c) => c.is_timestamp);
      const wmCol = tsCol?.source_field || editTable.watermark_column;
      const tableChanged = tableName !== job?.config?.table;
      const rescoped = scope.mode !== 'auto';
      const prevWm = job?.config?.incremental?.last_watermark;

      const incremental = wmCol ? {
        watermark_column: wmCol,
        watermark_type: editTable.watermark_type || 'datetime',
        // Reset the watermark (force re-backfill) when the table changed or
        // the user re-scoped; otherwise keep syncing from where we were.
        last_watermark: (tableChanged || rescoped) ? null : (prevWm ?? null),
      } : undefined;

      const cfg = {
        ...(job?.config || {}),
        table: tableName,
        field_mappings,
        mode: incremental ? 'incremental' : 'full',
      };
      if (incremental) cfg.incremental = incremental; else delete cfg.incremental;
      // Apply backfill scope (clears any stale one first).
      delete cfg.backfill_limit; delete cfg.backfill_range;
      Object.assign(cfg, scopeToBackfill(scope, null));
      body.config = cfg;
    }

    dispatch(updateJobRequest({ jobId, body }));
  };

  useEffect(() => {
    if (!updateSlice.response) return;
    dispatch(clearUpdateJob());
    navigate(`/datasources/${sourceId}`);
  }, [updateSlice.response, dispatch, navigate, sourceId]);

  useEffect(() => {
    if (updateSlice.error) {
      setError(updateSlice.error);
      setSaving(false);
      dispatch(clearUpdateJob());
    }
  }, [updateSlice.error, dispatch]);

  // Fire-and-forget: the triggerJob saga ignores errors, exactly like the
  // old inline call did.
  const runNow = () => {
    dispatch(triggerJobRequest({ jobId }));
  };

  const card = { border: `1px solid ${theme.palette.divider}`, borderRadius: 2, bgcolor: theme.palette.background.paper };
  const sectionLabel = { fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary', mb: 1 };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  if (!job) return <Box sx={{ maxWidth: 760, mx: 'auto', mt: 6 }}><Alert severity="error">{error || 'Job not found.'}</Alert></Box>;

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto' }}>
      <PageBreadcrumbs items={[
        { text: 'Data Sources', link: '/datasources', icon: <StorageIcon /> },
        { text: 'Source', link: `/datasources/${sourceId}` },
        { text: 'Edit sync' },
      ]} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ ...card, p: 2.5, mb: 2 }}>
        <Typography sx={sectionLabel}>Frequency</Typography>
        <ToggleButtonGroup size="small" exclusive value={frequency} onChange={(_, v) => v && setFrequency(v)} sx={{ flexWrap: 'wrap', mb: 2 }}>
          {FREQUENCIES.map((f) => <ToggleButton key={f.id} value={f.id} sx={{ textTransform: 'none', px: 1.75, fontSize: 13 }}>{f.label}</ToggleButton>)}
        </ToggleButtonGroup>

        <Box><FormControlLabel control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />} label={active ? 'Active' : 'Paused'} /></Box>

        {tablesList.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={sectionLabel}>Table</Typography>
            <TextField select size="small" fullWidth value={tableName} onChange={(e) => onSelectTable(e.target.value)} sx={{ mb: 2 }}>
              {tablesList.map((t) => (
                <MenuItem key={t.table_name} value={t.table_name} sx={{ fontSize: 13 }}>
                  {t.table_name}{t.row_estimate != null ? `  ·  ~${t.row_estimate.toLocaleString()} rows` : ''}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
      </Paper>

      {editTable && (
        <TableCard
          table={editTable}
          selectable={false}
          expanded
          onToggleExpand={() => {}}
          updateColumn={updateColumn}
          showScope
          scope={scope}
          setScope={(patch) => setScope((s) => ({ ...s, ...patch }))}
        />
      )}

      <Stack direction="row" spacing={1.5} sx={{ mt: 3 }} justifyContent="space-between">
        <Button onClick={runNow} startIcon={<PlayArrowIcon />} sx={{ textTransform: 'none' }}>Run now</Button>
        <Stack direction="row" spacing={1.5}>
          <Button onClick={() => navigate(`/datasources/${sourceId}`)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || (editTable && !editTable.timestamp_column)}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} sx={{ textTransform: 'none' }}>
            Save changes
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
